import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["popover", "list", "form", "count", "firstName", "lastName", "bio"]
  static values = {
    cardId: Number,
    projectId: Number,
    createUrl: String,
    toggleUrl: String
  }

  connect() {
    // Close popover when clicking outside
    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleOtherPopoverOpen = this.handleOtherPopoverOpen.bind(this)
    document.addEventListener('click', this.handleClickOutside)
    document.addEventListener('popover:open', this.handleOtherPopoverOpen)
  }

  disconnect() {
    document.removeEventListener('click', this.handleClickOutside)
    document.removeEventListener('popover:open', this.handleOtherPopoverOpen)
  }

  handleOtherPopoverOpen(event) {
    if (event.detail.controller !== this) {
      this.close()
    }
  }

  handleClickOutside(event) {
    if (this.hasPopoverTarget && !this.element.contains(event.target)) {
      this.close()
    }
  }

  toggle(event) {
    event.stopPropagation()
    if (this.hasPopoverTarget) {
      const wasHidden = this.popoverTarget.classList.contains('hidden')
      this.popoverTarget.classList.toggle('hidden')
      if (wasHidden) {
        document.dispatchEvent(new CustomEvent('popover:open', { detail: { controller: this } }))
      }
    }
  }

  close() {
    if (this.hasPopoverTarget) {
      this.popoverTarget.classList.add('hidden')
      this.hideForm()
    }
  }

  showForm(event) {
    event.preventDefault()
    if (this.hasFormTarget && this.hasListTarget) {
      this.listTarget.classList.add('hidden')
      this.formTarget.classList.remove('hidden')
      if (this.hasFirstNameTarget) {
        this.firstNameTarget.focus()
      }
    }
  }

  hideForm() {
    if (this.hasFormTarget && this.hasListTarget) {
      this.formTarget.classList.add('hidden')
      this.listTarget.classList.remove('hidden')
      this.clearForm()
    }
  }

  clearForm() {
    if (this.hasFirstNameTarget) this.firstNameTarget.value = ''
    if (this.hasLastNameTarget) this.lastNameTarget.value = ''
    if (this.hasBioTarget) this.bioTarget.value = ''
  }

  async createCharacter(event) {
    event.preventDefault()

    const firstName = this.hasFirstNameTarget ? this.firstNameTarget.value.trim() : ''
    const lastName = this.hasLastNameTarget ? this.lastNameTarget.value.trim() : ''
    const bio = this.hasBioTarget ? this.bioTarget.value.trim() : ''

    if (!firstName) {
      alert('First name is required')
      return
    }

    try {
      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          character: { first_name: firstName, last_name: lastName, bio: bio }
        })
      })

      if (response.ok) {
        const data = await response.json()
        this.addCharacterToList(data.character)
        this.hideForm()
        // Auto-toggle the new character onto this card
        this.toggleCharacter(data.character.id)
      } else {
        const error = await response.json()
        alert(error.errors?.join(', ') || 'Failed to create character')
      }
    } catch (err) {
      console.error('Failed to create character:', err)
    }
  }

  addCharacterToList(character) {
    const listContainer = this.listTarget.querySelector('[data-character-list]')
    if (listContainer) {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'w-full text-left px-3 py-2 rounded-lg text-white bg-emerald-500 hover:bg-emerald-600 transition mb-2'
      button.dataset.characterId = character.id
      button.dataset.action = 'click->character-popover#toggleCharacterClick'
      button.textContent = character.full_name
      listContainer.insertBefore(button, listContainer.querySelector('[data-add-new]'))
    }
  }

  async toggleCharacterClick(event) {
    event.preventDefault()
    const button = event.currentTarget
    const characterId = button.dataset.characterId
    const success = await this.toggleCharacter(characterId)

    // Only toggle visual state on success
    if (success) {
      if (button.classList.contains('bg-emerald-500')) {
        button.classList.remove('bg-emerald-500', 'hover:bg-emerald-600', 'text-white')
        button.classList.add('bg-gray-100', 'hover:bg-gray-200', 'text-gray-700')
      } else {
        button.classList.remove('bg-gray-100', 'hover:bg-gray-200', 'text-gray-700')
        button.classList.add('bg-emerald-500', 'hover:bg-emerald-600', 'text-white')
      }
    }
  }

  async toggleCharacter(characterId) {
    try {
      const response = await fetch(this.toggleUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ character_id: characterId })
      })

      if (response.ok) {
        const data = await response.json()
        if (this.hasCountTarget) {
          this.countTarget.textContent = data.count > 0 ? data.count : ''
        }
        return true
      }
      return false
    } catch (err) {
      console.error('Failed to toggle character:', err)
      return false
    }
  }
}
